import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Card, Col, Container, Form, FormControl, FormSelect, Row } from 'react-bootstrap'
import {
  CreditCardFill,
  FileEarmarkFill,
  Globe2,
  PersonFill,
  PersonVcardFill,
  PlusSquareFill,
} from 'react-bootstrap-icons'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { usePDF, Resolution } from 'react-to-pdf'
import { HistoryModal } from '../components/HistoryModal'
import KAccess from '../components/KAccess'
import KConfirm from '../components/KConfirm'
import KLayerNavigation from '../components/KLayerNavigation'
import KSwitch from '../components/KSwitch'
import { ValidationModal } from '../components/ValidationModal'
import {
  apiRequest,
  canEditKycForms,
  formatDDMMYYYY,
  getDeep,
  getUserAccess,
  isUserLoggedIn,
  setAccessToken,
  setUserAccess,
  updateNestedField,
} from '../helpers'
import {
  createFieldUpdate,
  EMPTY_COMMUNICATION,
  emptyAddress,
  emptyBank,
  emptyBeziehung,
  extractDynamicAdditional,
  extractStammdatenRoot,
  makeEmail,
  makePhone,
  makeUrl,
  mapTopographCompany,
  normalizeAddresses,
  normalizeBanks,
  normalizeBeziehung,
  normalizeCommunication,
  normalizeFiles,
  toArray,
  TopographCompany,
  uploadStammdatenFile,
} from '../helpers/stammdaten.helper'
import { t } from '../locale'
import {
  createClient,
  fetchAccess,
  fetchClientById,
  updateStammdatenClient,
  validatePinCode,
  validatePinCodeClient,
} from '../service/client'
import { fetchServiceCosts, ServiceCosts } from '../service/subscription'
import { getBankCountries, getCountries } from '../service/country'
import { getAnrede, getBanksByCountry, getLegalForms } from '../service/stammdaten'
import StammdatenAddress from '../stammdaten/StammdatenAddress'
import StammdatenBank from '../stammdaten/StammdatenBank'
import StammdatenBeziehungen from '../stammdaten/StammdatenBeziehungen'
import StammdatenCommunication from '../stammdaten/StammdatenCommunication'
import StammdatenUnthernamen from '../stammdaten/StammdatenUnthernamen'
import {
  AdditionalFormSchema,
  Communication,
  CompanyForm,
  FileUpload,
  HistoryEntry,
} from '../types/stammdaten.type'
import { useOutsideClick } from '../hooks/useOutsideClick'
import { useDebouncedCallback } from 'use-debounce'
import TopSection from '../stammdaten/StammdatenCompanyTopSection'
import KSignModal from '../components/KSign'
import { downloadKycFile } from '../helpers/downloadKycFile'
import v from '../validators'
import KFilePreview, { PreviewTarget } from '../components/KFilePreview'

const SECTION_KEYS = {
  UNTERNEHMEN: 'unternehmen',
  ADDRESS: 'address',
  COMMUNICATION: 'communication',
  BANK: 'bank',
  FINANCE: 'finance',
  RELATIONSHIPS: 'relationships',
  DOCUMENTS_LAWYER: 'documentsLawyer',
  DOCUMENTS_CLIENT: 'documentsClient',
  ADDITIONAL: 'additional',
} as const

const MemoizedStammdatenUnthernamen = React.memo(StammdatenUnthernamen)
export const MemoizedStammdatenAddress = React.memo(StammdatenAddress)
export const MemoizedStammdatenCommunication = React.memo(StammdatenCommunication)
export const MemoizedStammdatenBank = React.memo(StammdatenBank)
export const MemoizedStammdatenBeziehungen = React.memo(StammdatenBeziehungen)

const useCompanyData = (clientId: string) => {
  const [companyData, setCompanyData] = useState<CompanyForm>({
    isStammdaten: true,
    clientNumber: '',
    client_since: '',
    client_to: '',
    anrede: '',
    name: '',
    legal_form: '',
    client_note: '',
    corporate_purpose: '',
    location_of_headquarters: '',
    country_of_headquarters: '',
    founding_date: '',
    nationality: '',
    national_law: {
      eu_vat_id_country_code: '',
      eu_vat_id_number: '',
    },
    registry_court: {
      register_entry: '',
      commercial_register: '',
      register_number: '',
      commercial_register_company_name: '',
    },
    address: [emptyAddress()],
    communication: EMPTY_COMMUNICATION,
    bank: [emptyBank()],
    beziehung: [emptyBeziehung()],
    additional: { dynamic: {} },
    files: [],
    filesClient: [],
  })

  const [requiredFields, setRequiredFields] = useState({
    client_since: false,
    client_to: false,
    anrede: false,
    unternehmensform: false,
    client_note: false,
    corporate_purpose: false,
    location_of_headquarters: false,
    country_of_headquarters: false,
    founding_date: false,
    nationality: false,
    branche: { description: false },
    national_law: {
      eu_vat_id_country_code: false,
      federal_state: false,
    },
    registry_court: {
      register_entry: false,
      commercial_register: false,
      register_number: false,
      commercial_register_company_name: false,
    },
    address: {
      valid_since: false,
      valid_to: false,
      address_suffix: false,
      type: false,
      zip_code: false,
      city: false,
      country: false,
      shipping_supplement: false,
      note: false,
    },
    communication: {
      phone: false,
      email: false,
      url: false,
    },
    bank: {
      valid_since: false,
      valid_to: false,
      country: false,
      bank_name: false,
      iban: false,
      account_number: false,
      account_holder: false,
      note: false,
    },
    beziehung: {
      hatIst: false,
      name: false,
      addressee_type: false,
      anrede: false,
      tax_authority: {
        country_code: false,
        tax_authority_name: false,
        tax_number: false,
      },
      address: {
        type: false,
        zip_code: false,
        city: false,
        country: false,
        phone: false,
        email: false,
      },
    },
  })

  const fetchRequiredFields = useCallback(async () => {
    if (!clientId) return

    try {
      const res = await apiRequest(
        `${process.env.REACT_APP_API_URL}/stammdaten/requiredBy?type=company`,
        'GET'
      )
      if (res.ok) {
        res.result.fields.forEach((element: any) => {
          const fieldPath = element.name
          const value = element.required
          setRequiredFields(prev => updateNestedField(prev, fieldPath, value))
        })
      }
    } catch (error) {
      console.error('Failed to fetch required fields:', error)
    }
  }, [clientId])

  useEffect(() => {
    if (clientId) {
      fetchRequiredFields()
    }
  }, [clientId, fetchRequiredFields])

  return { companyData, setCompanyData, requiredFields }
}

const useExpandedSections = () => {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    [SECTION_KEYS.UNTERNEHMEN]: false,
    [SECTION_KEYS.ADDRESS]: false,
    [SECTION_KEYS.COMMUNICATION]: false,
    [SECTION_KEYS.BANK]: false,
    [SECTION_KEYS.FINANCE]: false,
    [SECTION_KEYS.RELATIONSHIPS]: false,
    [SECTION_KEYS.DOCUMENTS_LAWYER]: false,
    [SECTION_KEYS.DOCUMENTS_CLIENT]: false,
    [SECTION_KEYS.ADDITIONAL]: false,
  })

  const toggleSection = useCallback((sectionKey: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }))
  }, [])

  const expandAllSections = useCallback(() => {
    setExpandedSections(prev =>
      Object.keys(prev).reduce(
        (acc, key) => ({
          ...acc,
          [key]: true,
        }),
        {} as Record<string, boolean>
      )
    )
  }, [])

  return { expandedSections, toggleSection, expandAllSections }
}

export default function StammdatenCompany() {
  const navigate = useNavigate()
  const { clientId } = useParams()

  // The shared-client view is reached through StammdatenPinAccess, which navigates
  // to the plain `/client/stammdaten/company/:clientId` route (no `:email` segment),
  // so it can only be told apart from an internal team member by the absence of a
  // logged-in user session. `clientEmail` is the address PIN access stored for them.
  const isSharedClient = !isUserLoggedIn()
  const clientEmail = localStorage.getItem('email') || ''

  const canEdit = canEditKycForms()

  const [serviceCosts, setServiceCosts] = useState<ServiceCosts | null>(null)
  const [costConfirmed, setCostConfirmed] = useState(clientId !== 'new')

  const { toPDF, targetRef } = usePDF({ filename: 'stammdaten.pdf', resolution: Resolution.LOW })
  const [searchParams] = useSearchParams()
  const pdf = (searchParams.get('pdf') ?? '').toLowerCase() === 'true'
  const pdfGeneratedRef = useRef(false)

  const { companyData, setCompanyData, requiredFields } = useCompanyData(clientId || '')
  const { expandedSections, toggleSection, expandAllSections } = useExpandedSections()

  const [openSchemas, setOpenSchemas] = useState<Record<string, boolean>>({})
  const [additionalForms, setAdditionalForms] = useState<AdditionalFormSchema[]>([])
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false)

  const [anredeOptions, setAnredeOptions] = useState([])
  const [legalForms, setLegalForms] = useState([])
  const [selectedFile, setSelectedFile] = useState<File | null>(null)

  const [countries, setCountries] = useState([])
  const [availableBanks, setAvailableBanks] = useState<any[]>([])
  const [bankCountries, setBankCountries] = useState([])

  const [datePickerClientSince, setDatePickerClientSince] = useState(false)
  const [datePickerClientTo, setDatePickerClientTo] = useState(false)

  const [historyModal, setHistoryModal] = useState({
    isOpen: false,
    title: '',
    entries: [] as HistoryEntry[],
    options: undefined as { value: any; label: string }[] | undefined,
  })

  const [validationModal, setValidationModal] = useState({
    agreed: false,
    isOpen: false,
    field: '',
  })

  const [pin, setPin] = useState('')
  const [access, setAccess] = useState(false)
  const [pinRequired, setPinRequired] = useState(true)
  const [previewTarget, setPreviewTarget] = useState<PreviewTarget | null>(null)

  const [topographCompany, setTopographCompany] = useState<TopographCompany | null>(null)
  const topographPrefilledRef = useRef(false)

  const banks = useMemo(() => companyData.bank.map((_, i) => i), [companyData.bank])
  const beziehungs = useMemo(() => companyData.beziehung.map((_, i) => i), [companyData.beziehung])

  const creatingClientRef = useRef(false)
  const realClientIdRef = useRef<string | null>(null)

  const [signModal, setSignModal] = useState(false)
  const [pendingSignDocument, setPendingSignDocument] = useState<string | null>(null)
  const [currentDocument, setCurrentDocument] = useState('')
  const [accessForSign, setAccessForSign] = useState(false)

  const [documentType, setDocumentType] = useState('')

  const ref = useOutsideClick(() => {
    setDatePickerClientTo(false)
    setDatePickerClientSince(false)
  })

  useEffect(() => {
    if (!clientId) return
    let alive = true

    const fetchClientData = async () => {
      try {
        if (clientId === 'new') return

        const resp = await fetchClientById(clientId)
        if (!alive) return

        const [base, addresses, communication, banks, beziehungs, files, filesClient] =
          await Promise.all([
            Promise.resolve(buildFormFromApi(resp)),
            Promise.resolve(normalizeAddresses(resp)),
            Promise.resolve(normalizeCommunication(resp, true)),
            Promise.resolve(normalizeBanks(resp)),
            Promise.resolve(normalizeBeziehung(resp)),
            Promise.resolve(normalizeFiles(resp, 'files')),
            Promise.resolve(normalizeFiles(resp, 'filesClient')),
          ])

        const dynamicAdditional = extractDynamicAdditional(resp)

        if (alive)
          setCompanyData({
            ...base,
            address: addresses.length > 0 ? addresses : [emptyAddress()],
            communication,
            bank: banks.length > 0 ? banks : [emptyBank()],
            beziehung: beziehungs.length > 0 ? beziehungs : [emptyBeziehung()],
            additional: { dynamic: dynamicAdditional },
            files,
            filesClient,
          })

        if (alive) setTopographCompany(resp?.client?.data?.topographData?.company ?? null)
      } catch (e) {
        console.error(e)
      }
    }

    fetchClientData()
    return () => {
      alive = false
    }
  }, [clientId])

  useEffect(() => {
    if (topographPrefilledRef.current) return
    if (!topographCompany || legalForms.length === 0 || countries.length === 0) return

    topographPrefilledRef.current = true

    const mapped = mapTopographCompany(topographCompany, legalForms)
    const updates: Array<[string, string]> = []

    for (const [path, value] of Object.entries(mapped)) {
      if (!value) continue
      const current = getDeep(companyData, path)
      if (current !== undefined && current !== null && String(current).trim() !== '') continue
      if (path === 'legal_form' && !legalForms.some((o: any) => o.value === value)) continue
      if (
        (path === 'address.0.country' || path === 'country_of_headquarters') &&
        !countries.some((o: any) => o.value === value)
      )
        continue
      updates.push([path, value])
    }

    if (updates.length === 0) return

    setCompanyData(prev => {
      let next = prev
      for (const [path, value] of updates) next = updateNestedField(next, path, value)
      return next
    })
    updates.forEach(([path, value]) => saveFieldNow(path, value))
  }, [topographCompany, legalForms, countries, companyData])

  useEffect(() => {
    fetchAdditionalForms()
    fetchCountries()
    fetchBankCountries()
    fetchLegalForms()
    fetchAnrede()
  }, [])

  useEffect(() => {
    if (clientId === 'new') {
      fetchServiceCosts().then(setServiceCosts)
    }
  }, [clientId])

  const generatePDF = () => {
    expandAllSections()
    setIsGeneratingPDF(true)

    const allOpenSchemas: any = {}
    additionalForms.forEach(schema => {
      if (schema?.include && schema.stammdatenType === 'company') {
        allOpenSchemas[schema._id] = true
      }
    })
    setOpenSchemas(allOpenSchemas)

    setTimeout(() => {
      toPDF()
      setIsGeneratingPDF(false)
    }, 2000)
  }

  useEffect(() => {
    if (pdf && !pdfGeneratedRef.current) {
      pdfGeneratedRef.current = true
      generatePDF()
    }
  }, [pdf, additionalForms])

  const requestSignAccess = (documentName: string) => {
    setPendingSignDocument(documentName)
    fetchAccess(localStorage.getItem('email')).then(result => {
      if (!!result.accessToken) {
        if (clientId) localStorage.setItem('clientId', clientId)
        setAccessToken(result.accessToken)
        setPin(result.pinCode)
        setPinRequired(result.pinRequired !== false)
        setAccessForSign(true)
      }
    })
  }

  const debouncedSave = useDebouncedCallback((field: string, value: any) => {
    saveFieldNow(field, value)
  }, 800)

  const handleAddAddress = () =>
    setCompanyData(prev => {
      const updatedAddress = [...prev.address, emptyAddress()]
      saveFieldNow('address', updatedAddress)
      return { ...prev, address: updatedAddress }
    })

  const handleAddBank = () =>
    setCompanyData(prev => {
      const updatedBank = [...prev.bank, emptyBank()]
      saveFieldNow('bank', updatedBank)
      return { ...prev, bank: updatedBank }
    })

  const handleRemoveBank = (indexToRemove: number) => {
    debouncedSave.cancel()
    setCompanyData(prev => {
      const updatedBank = prev.bank.filter((_, index) => index !== indexToRemove)
      saveFieldNow('bank', updatedBank)
      return { ...prev, bank: updatedBank }
    })
  }

  const handleAddBeziehungen = () =>
    setCompanyData(prev => {
      const updatedBeziehung = [...prev.beziehung, emptyBeziehung()]
      saveFieldNow('beziehung', updatedBeziehung)
      return { ...prev, beziehung: updatedBeziehung }
    })

  const handleRemoveBeziehung = (indexToRemove: number) => {
    debouncedSave.cancel()
    setCompanyData(prev => {
      const updatedBeziehung = prev.beziehung.filter((_, index) => index !== indexToRemove)
      saveFieldNow('beziehung', updatedBeziehung)
      return { ...prev, beziehung: updatedBeziehung }
    })
  }

  const handleCommunicationRemoveRow = (kind: keyof Communication, indexToRemove: number) => {
    debouncedSave.cancel()
    setCompanyData(prev => {
      const updated = prev.communication[kind].filter((_, index) => index !== indexToRemove)
      const updatedCommunication = { ...prev.communication, [kind]: updated }
      saveFieldNow('communication', updatedCommunication)
      return { ...prev, communication: updatedCommunication }
    })
  }

  const handleCommunicationAddRow = (kind: keyof Communication) => {
    const creatorMap = {
      phone: makePhone,
      email: makeEmail,
      url: makeUrl,
    } as const

    setCompanyData(prev => ({
      ...prev,
      communication: {
        ...prev.communication,
        [kind]: [...prev.communication[kind], creatorMap[kind]()],
      },
    }))
  }

  const handleRemoveAddress = (indexToRemove: number) => {
    debouncedSave.cancel()
    setCompanyData(prev => {
      const updatedAddress = prev.address.filter((_, index) => index !== indexToRemove)
      saveFieldNow('address', updatedAddress)
      return { ...prev, address: updatedAddress }
    })
  }

  function buildFormFromApi(apiData: any): CompanyForm {
    const s = extractStammdatenRoot(apiData) ?? {}
    let next: CompanyForm = JSON.parse(JSON.stringify(companyData))

    const SIMPLE_PATHS = [
      'isStammdaten',
      'clientNumber',
      'client_since',
      'client_to',
      'anrede',
      'name',
      'legal_form',
      'client_note',
      'founding_date',
      'corporate_purpose',
      'location_of_headquarters',
      'country_of_headquarters',
      'nationality',
      'national_law.eu_vat_id_country_code',
      'national_law.eu_vat_id_number',
      'registry_court.register_entry',
      'registry_court.commercial_register',
      'registry_court.register_number',
      'registry_court.commercial_register_company_name',
    ]

    for (const p of SIMPLE_PATHS) {
      const v = getDeep(s, p)
      if (v !== undefined) next = updateNestedField(next, p, v)
    }

    let addr = s?.address
    if (addr && typeof addr === 'object' && Object.keys(addr).every(k => /^\d+$/.test(k))) {
      addr = Object.values(addr)
    } else {
      addr = []
    }

    next = { ...next, address: addr }

    const nl = getDeep(s, 'national_law')
    if (nl) next = updateNestedField(next, 'national_law', { ...next.national_law, ...nl })
    const rc = getDeep(s, 'registry_court')
    if (rc) next = updateNestedField(next, 'registry_court', { ...next.registry_court, ...rc })

    return next
  }

  const checkPin = async (pinCode: number) => {
    try {
      const result = await validatePinCodeClient(pinCode, clientEmail)

      if (result?.accessToken) {
        setAccessToken(result.accessToken)
        setAccess(false)
      }
    } catch (error) {
      console.error('Error validating pin code:', error)
    }
  }

  const checkPinForSign = async (pinCode: number) => {
    try {
      const result = await validatePinCode(pinCode)

      if (result?.userAccess) {
        setUserAccess(result.userAccess)
        setAccessForSign(false)

        if (pendingSignDocument) {
          setCurrentDocument(pendingSignDocument)
          setPendingSignDocument(null)
          setSignModal(true)
        }
      }
    } catch (error) {
      console.error('Error validating pin code:', error)
    }
  }

  const fetchAnrede = async () => {
    try {
      const options = await getAnrede()
      setAnredeOptions(options)
    } catch (err) {
      console.error('Fetch anrede error:', err)
    }
  }

  const fetchLegalForms = async () => {
    try {
      const options = await getLegalForms()
      setLegalForms(options)
    } catch (err) {
      console.error('Fetch legal forms error:', err)
    }
  }

  const fetchAdditionalForms = async () => {
    apiRequest(`${process.env.REACT_APP_API_URL}/additional/all`, 'GET').then(r => {
      setAdditionalForms(r.docs)
    })
  }

  const fetchCountries = async () => {
    try {
      const response = await getCountries()
      setCountries(response.countries.map((c: any) => ({ value: c.code, label: c.name })))
    } catch (err) {
      console.error('Fetch countries: ', err)
    }
  }

  const fetchBankCountries = async () => {
    try {
      const response = await getBankCountries()
      console.log('Fetched bank countries', response)
      setBankCountries(response.map((c: any) => ({ value: c.code, label: c.name })))
    } catch (err) {
      console.error('Fetch bank countries: ', err)
    }
  }

  const history = async (
    fieldPath: string,
    label: string,
    options?: { value: any; label: string }[]
  ) => {
    if (!clientId) {
      alert('Missing clientId')
      return
    }

    apiRequest(
      `${process.env.REACT_APP_API_URL}/clients/${clientId}/stammdaten/history?field=${encodeURIComponent(fieldPath)}&order=desc`,
      'GET'
    ).then(res => {
      setHistoryModal({
        entries: res.history || [],
        title: label,
        isOpen: true,
        options,
      })
    })
  }

  const handleFileDownload = async (filename: string, type: string) => {
    await downloadKycFile(clientId || '', companyData.name || '', type, filename)
  }

  const handleFileView = (filename: string, type: string) => {
    setPreviewTarget({
      clientId: clientId || '',
      firmename: companyData.name || '',
      type,
      filename,
    })
  }

  const handleUpload = useCallback(
    async (type: string, fieldPath?: string) => {
      if (!selectedFile) return alert(t('chooseFile'))

      if (companyData.name === '') {
        return alert(t('setNameFirst'))
      }
      try {
        const entry = await uploadStammdatenFile({
          file: selectedFile,
          clientId: clientId || '',
          type,
          name: companyData.name,
          email: isSharedClient ? clientEmail : undefined,
        })

        if (type === 'files') {
          setCompanyData(prev => {
            const newFiles = [...(prev.files || []), entry]
            const next = { ...prev, files: newFiles }

            saveFieldNow('files', newFiles)

            return next
          })
        } else if (type === 'filesClient') {
          setCompanyData(prev => {
            const newFiles = [...(prev.filesClient || []), entry]
            const next = { ...prev, filesClient: newFiles }

            saveFieldNow('filesClient', newFiles)

            return next
          })
        } else if (type === 'additionalFormFiles' && fieldPath) {
          setCompanyData(prev => {
            const currentFiles = toArray(getDeep(prev, fieldPath))
            const newFiles = [...currentFiles, entry]
            const next = updateNestedField(prev, fieldPath, newFiles)

            saveFieldNow(fieldPath, newFiles)

            return next
          })
        }

        alert('Uploaded success')
        setSelectedFile(null)
      } catch (err) {
        console.error(err)
        alert('Upload error')
      }
    },
    [clientId, companyData.name, selectedFile, isSharedClient, clientEmail, companyData.files]
  )

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      setSelectedFile(e.target.files[0])
    }
  }

  const handleToggleDifferent = async (idx: number, checked: boolean) => {
    if (!canEdit) return

    setCompanyData(prev => {
      let next = updateNestedField(prev, `address.${idx}.use_different_delivery_addressee`, checked)
      if (!checked) {
        next = updateNestedField(next, `address.${idx}.abbrev_salutation`, '')
        next = updateNestedField(next, `address.${idx}.different_person`, '')
        next = updateNestedField(next, `address.${idx}.delivery_addressee`, '')
      }
      return next
    })

    if (!clientId || clientId === 'new') return
    const patch: Record<string, any> = {}
    createFieldUpdate(patch, `address.${idx}.use_different_delivery_addressee`, checked)
    if (!checked) {
      createFieldUpdate(patch, `address.${idx}.abbrev_salutation`, '')
      createFieldUpdate(patch, `address.${idx}.different_person`, '')
      createFieldUpdate(patch, `address.${idx}.delivery_addressee`, '')
    }
    await updateStammdatenClient({ clientId, isStammdaten: true, stammdaten: patch })
  }

  const handleBeziehungenTypeChange = async (idx: number, value: string) => {
    setCompanyData(prev => {
      let next = updateNestedField(prev, `beziehung.${idx}.addressee_type`, value)
      debouncedSave(`beziehung.${idx}.addressee_type`, value)
      return next
    })
  }

  const handleDateChange = async (id: string, date: Date | null) => {
    const asString = formatDDMMYYYY(date)

    const input = document.getElementById(id) as HTMLInputElement | null
    input?.setCustomValidity('')

    setCompanyData(prev => updateNestedField(prev, id, asString))

    if (!clientId) return
    await saveFieldNow(id, asString)
  }
  const handleChangeLocal = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const { id, type, name } = e.target
      const value = type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value

      if (id.includes('additional')) {
        const [schemaName, fieldName, optName] = name.split('.')

        if (type === 'checkbox') {
          setCompanyData(prev =>
            updateNestedField(
              prev,
              `additional.dynamic.${schemaName}.${fieldName}.${optName}`,
              value
            )
          )
          debouncedSave(`additional.dynamic.${schemaName}.${fieldName}.${optName}`, value)
        } else {
          setCompanyData(prev =>
            updateNestedField(prev, `additional.dynamic.${schemaName}.${fieldName}`, value)
          )
          debouncedSave(`additional.dynamic.${schemaName}.${fieldName}`, value)
        }
      } else {
        setCompanyData(prev => updateNestedField(prev, id, value))
        debouncedSave(id, value)

        if (id.includes('bank.') && id.endsWith('.country')) {
          const bankIndex = id.split('.')[1]
          setCompanyData(prev => updateNestedField(prev, `bank.${bankIndex}.bank_name`, ''))
          if (value && typeof value === 'string') {
            getBanksByCountry(value)
              .then(banks => setAvailableBanks(banks))
              .catch(() => setAvailableBanks([]))
          } else {
            setAvailableBanks([])
          }
        }
      }
    },
    []
  )

  const handleDateInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { id, value } = e.currentTarget
      const isValid = value === '' || v.date(value)

      e.currentTarget.setCustomValidity(isValid ? '' : t('invalidDate'))

      setCompanyData(prev => updateNestedField(prev, id, value))

      if (isValid) {
        debouncedSave(id, value)
      }
    },
    [debouncedSave]
  )

  const saveFieldNow = async (id: string, value?: unknown) => {
    if (!canEdit) return

    let effectiveClientId = clientId === 'new' ? realClientIdRef.current : clientId

    if (clientId === 'new' && !realClientIdRef.current) {
      if (creatingClientRef.current) return
      creatingClientRef.current = true
      try {
        const client = await createClient({ isPerson: 0, isStammdaten: true })
        realClientIdRef.current = client.clientId
        effectiveClientId = client.clientId
        navigate(`/client/stammdaten/company/${client.clientId}`, { replace: true })
      } catch (e) {
        console.error('Error creating client', e)
        creatingClientRef.current = false
        return
      }
      creatingClientRef.current = false
    }

    if (!effectiveClientId) return

    try {
      const fieldValue = value === undefined ? getDeep(companyData, id) : value
      let stammdatenPatch = {}
      if (id === 'files' || id === 'filesClient') {
        stammdatenPatch = updateNestedField({}, id, value)
      } else {
        createFieldUpdate(stammdatenPatch, id, fieldValue)
      }

      await updateStammdatenClient({
        clientId: effectiveClientId,
        isStammdaten: true,
        userEmail: localStorage.getItem('email') || '',
        userName: getUserAccess() ? getUserAccess().name : '',
        stammdaten: stammdatenPatch,
      })
    } catch (e) {
      console.error('Save error', e)
    }
  }

  const toggleSchema = (id: string) => setOpenSchemas(prev => ({ ...prev, [id]: !prev[id] }))

  const handleAgreementChange = async (value: boolean) => {
    setValidationModal({
      agreed: value,
      isOpen: validationModal.isOpen,
      field: validationModal.field,
    })

    if (value) {
      handleSubmit()
    }
  }

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault()
    expandAllSections()

    const allOpenSchemas: any = {}
    additionalForms.forEach(schema => {
      if (schema?.include && schema.stammdatenType === 'company') {
        allOpenSchemas[schema._id] = true
      }
    })
    setOpenSchemas(allOpenSchemas)

    setTimeout(() => {
      const form = targetRef.current as HTMLFormElement

      if (!form.checkValidity()) {
        setValidationModal({
          isOpen: validationModal.isOpen,
          field: validationModal.field,
          agreed: false,
        })

        form.reportValidity()

        const firstInvalidControl = form.querySelector<
          HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
        >('input:invalid, select:invalid, textarea:invalid')

        if (firstInvalidControl?.validity.customError) {
          setValidationModal({
            isOpen: true,
            field: firstInvalidControl.validationMessage,
            agreed: validationModal.agreed,
          })
          return
        }

        if (!form.checkValidity()) {
          const firstInvalid = form.querySelector<HTMLElement>(':invalid')

          if (firstInvalid) {
            const input = firstInvalid as HTMLInputElement

            setValidationModal({
              isOpen: true,
              field: validationModal.field,
              agreed: validationModal.agreed,
            })

            setValidationModal({
              isOpen: true,
              field: input.name || '',
              agreed: validationModal.agreed,
            })
          }
        }

        return
      }

      if (isUserLoggedIn()) {
        navigate('/client/stammdaten')
      } else {
        navigate('/user/login')
      }
    }, 100)
  }

  const generateSingleFormPDF = async (schemaId: string, schemaName: string) => {
    setOpenSchemas(prev => ({
      ...prev,
      [schemaId]: true,
    }))

    setTimeout(async () => {
      const formElement = document.getElementById(`schema-${schemaId}-content`)
      if (formElement) {
        const html2canvas = (await import('html2canvas')).default
        const jsPDF = (await import('jspdf')).default

        const canvas = await html2canvas(formElement, {
          scale: 2,
          logging: false,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
        })

        const imgData = canvas.toDataURL('image/png')
        const imgWidth = 208 // A4 width in mm with margins
        const pageHeight = 285 // A4 height in mm with margins (reduced for header)
        const imgHeight = (canvas.height * imgWidth) / canvas.width

        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4',
        })

        // Helper function to add header to each page
        const addHeader = () => {
          pdf.setFontSize(10)
          pdf.setTextColor(100)
          pdf.text(`Client: ${companyData.clientNumber || 'N/A'}`, 10, 8)
          pdf.text(`Name: ${companyData.name || 'N/A'}`, 10, 14)
          pdf.setTextColor(0)
        }

        let heightLeft = imgHeight
        let position = 20 // Start below header

        // Add first page with header
        addHeader()
        pdf.addImage(imgData, 'PNG', 1, position, imgWidth, imgHeight)
        heightLeft -= pageHeight

        // Add remaining pages
        while (heightLeft > 0) {
          position = heightLeft - imgHeight + 20 // Account for header
          pdf.addPage()
          addHeader()
          pdf.addImage(imgData, 'PNG', 1, position, imgWidth, imgHeight)
          heightLeft -= pageHeight
        }

        pdf.save(`${schemaName}.pdf`)
      }
    }, 1000)
  }

  const handleClose = () => {
    setSignModal(false)
  }

  return (
    <>
      <Container>
        <Row>
          <div className='sidebar col-md-3 col-lg-2 p-0'>
            {!isSharedClient && <KLayerNavigation logged={isUserLoggedIn()} />}
          </div>
          <form
            ref={targetRef}
            onSubmit={handleSubmit}
            className='col-md-9 col-lg-10 ps-2 ps-md-3 px-md-4 md-4'
          >
            <fieldset disabled={!canEdit} className='border-0 p-0 m-0' style={{ minWidth: 0 }}>
              {!canEdit && (
                <div className='alert alert-info mt-3 ms-3'>{t('readOnlyNoPermission')}</div>
              )}
              <Row className='text-muted ms-3'>
                {t('client')}: {companyData.clientNumber}
                <br />
                {t('companyName')}: {companyData.name}
              </Row>
              <Card className='border-0 mb-5'>
                <Card.Body>
                  <Col className='col-sm-7'>
                    <Card.Title>
                      <h4 className='fw-bold'>
                        {t('KYC - Know Your Customer ')}
                        <small className='text-muted'>{t('Stammdaten')}</small>
                      </h4>
                    </Card.Title>
                  </Col>
                  <hr />

                  <TopSection
                    companyData={companyData}
                    handleChangeLocal={handleChangeLocal}
                    handleDateInputChange={handleDateInputChange}
                    anredeOptions={anredeOptions}
                    legalForms={legalForms}
                    requiredFields={requiredFields}
                    history={history}
                    datePickerClientSince={datePickerClientSince}
                    datePickerClientTo={datePickerClientTo}
                    setDatePickerClientSince={setDatePickerClientSince}
                    setDatePickerClientTo={setDatePickerClientTo}
                    handleDateChange={handleDateChange}
                    ref={ref}
                  />

                  {/* ===================== Unthernamen ===================== */}
                  <Row className='mt-4'>
                    <h5
                      className='bg-collapse text-white p-2 rounded mb-3 cursor-pointer d-flex align-items-center'
                      onClick={() => toggleSection(SECTION_KEYS.UNTERNEHMEN)}
                    >
                      <PersonFill />
                      &nbsp;{t('Unternehmensdaten')}
                      {
                        <b className='ms-auto'>
                          {expandedSections[SECTION_KEYS.UNTERNEHMEN] ? '▼' : '►'}
                        </b>
                      }
                    </h5>
                  </Row>

                  {expandedSections[SECTION_KEYS.UNTERNEHMEN] && (
                    <MemoizedStammdatenUnthernamen
                      form={companyData}
                      handleChangeLocal={handleChangeLocal}
                      handleDateInputChange={handleDateInputChange}
                      history={history}
                      options={countries}
                      handleDateChange={handleDateChange}
                      required={requiredFields}
                    />
                  )}

                  {/* ===================== Address ===================== */}
                  <Row className='mt-2'>
                    <h5
                      className='bg-collapse text-white p-2 rounded mb-3 cursor-pointer d-flex align-items-center'
                      onClick={() => toggleSection(SECTION_KEYS.ADDRESS)}
                    >
                      <PersonVcardFill />
                      &nbsp;{t('Adressen')}
                      {
                        <b className='ms-auto'>
                          {expandedSections[SECTION_KEYS.ADDRESS] ? '▼' : '►'}
                        </b>
                      }
                    </h5>
                  </Row>

                  {expandedSections[SECTION_KEYS.ADDRESS] && (
                    <MemoizedStammdatenAddress
                      form={companyData}
                      options={countries}
                      handleChangeLocal={handleChangeLocal}
                      handleDateInputChange={handleDateInputChange}
                      history={history}
                      handleRemoveAddress={handleRemoveAddress}
                      handleDateChange={handleDateChange}
                      handleToggleDifferent={handleToggleDifferent}
                      handleAddAddress={handleAddAddress}
                      required={requiredFields}
                    />
                  )}

                  {/* ===================== Kommunikation ===================== */}
                  <Row className='mt-2'>
                    <h5
                      className='bg-collapse text-white p-2 rounded mb-3 cursor-pointer d-flex align-items-center'
                      onClick={() => toggleSection(SECTION_KEYS.COMMUNICATION)}
                    >
                      <Globe2 />
                      &nbsp;{t('Kommunikation')}
                      <b className='ms-auto'>
                        {expandedSections[SECTION_KEYS.COMMUNICATION] ? '▼' : '►'}
                      </b>
                    </h5>
                  </Row>

                  {expandedSections[SECTION_KEYS.COMMUNICATION] && (
                    <MemoizedStammdatenCommunication
                      form={companyData}
                      handleChangeLocal={handleChangeLocal}
                      history={history}
                      handleAddRow={handleCommunicationAddRow}
                      handleRemoveRow={handleCommunicationRemoveRow}
                      required={requiredFields}
                    />
                  )}

                  {/* ===================== Banken ===================== */}
                  <Row className='mt-2'>
                    <h5
                      className='bg-collapse text-white p-2 rounded mb-3 cursor-pointer d-flex align-items-center'
                      onClick={() => toggleSection(SECTION_KEYS.BANK)}
                    >
                      <CreditCardFill />
                      &nbsp;{t('Banken')}
                      <b className='ms-auto'>{expandedSections[SECTION_KEYS.BANK] ? '▼' : '►'}</b>
                    </h5>
                  </Row>
                  {expandedSections[SECTION_KEYS.BANK] && (
                    <MemoizedStammdatenBank
                      form={companyData}
                      handleChangeLocal={handleChangeLocal}
                      handleDateInputChange={handleDateInputChange}
                      history={history}
                      options={bankCountries}
                      banks={banks}
                      handleDateChange={handleDateChange}
                      handleAddBank={handleAddBank}
                      handleRemoveBank={handleRemoveBank}
                      required={requiredFields}
                      availableBanks={availableBanks}
                    />
                  )}

                  {/* ===================== Beziehungen ===================== */}
                  <Row className='mt-2'>
                    <h5
                      className='bg-collapse text-white p-2 rounded mb-3 cursor-pointer d-flex align-items-center'
                      onClick={() => toggleSection(SECTION_KEYS.RELATIONSHIPS)}
                    >
                      <CreditCardFill />
                      &nbsp;{t('Beziehungen')}
                      <b className='ms-auto'>
                        {expandedSections[SECTION_KEYS.RELATIONSHIPS] ? '▼' : '►'}
                      </b>
                    </h5>
                  </Row>

                  {expandedSections[SECTION_KEYS.RELATIONSHIPS] && (
                    <MemoizedStammdatenBeziehungen
                      form={companyData}
                      handleChangeLocal={handleChangeLocal}
                      handleDateInputChange={handleDateInputChange}
                      history={history}
                      options={countries}
                      beziehungs={beziehungs}
                      handleBeziehungenTypeChange={handleBeziehungenTypeChange}
                      handleAddBeziehungen={handleAddBeziehungen}
                      handleRemoveBeziehung={handleRemoveBeziehung}
                      handleDateChange={handleDateChange}
                      required={requiredFields}
                    />
                  )}

                  <Row className='mt-4'>
                    <h5
                      className='bg-collapse text-white p-2 rounded mb-3 cursor-pointer d-flex align-items-center'
                      onClick={() => toggleSection(SECTION_KEYS.DOCUMENTS_LAWYER)}
                    >
                      <FileEarmarkFill />
                      &nbsp;{t('DokumenteKanzlei')}
                      {
                        <b className='ms-auto'>
                          {expandedSections[SECTION_KEYS.DOCUMENTS_LAWYER] ? '▼' : '►'}
                        </b>
                      }
                    </h5>
                  </Row>
                  {expandedSections[SECTION_KEYS.DOCUMENTS_LAWYER] && (
                    <div id='documents-data' className='mb-4'>
                      <Row className='mt-2 border-bottom pb-2'>
                        <Col className='col-sm-3'>
                          <b>{t('users')}</b>
                        </Col>
                        <Col className='col-sm-3'>
                          <b>{t('datumHochladen')}</b>
                        </Col>
                        <Col className='col-sm-3'>
                          <b>{t('file')}</b>
                        </Col>
                        <Col className='col-sm-3'>
                          <b>{t('actions')}</b>
                        </Col>
                      </Row>
                      {companyData.files.map((file, idx) => (
                        <Row key={idx} className='mt-2 border-bottom pb-2'>
                          <Col className='col-sm-3'>{file.user}</Col>
                          <Col className='col-sm-3'>{file.date}</Col>
                          <Col className='col-sm-3'>{file.name}</Col>
                          <Col className='col-sm-3'>
                            <Button
                              variant='outline-secondary'
                              size='sm'
                              className='me-1'
                              title={t('preview')}
                              onClick={() => handleFileView(file.name, 'stammdaten_files')}
                            >
                              <i className='bi bi-eye'></i>
                            </Button>
                            <Button
                              variant='outline-primary'
                              size='sm'
                              className='me-1'
                              title={t('download')}
                              onClick={() => handleFileDownload(file.name, 'stammdaten_files')}
                            >
                              <i className='bi bi-download'></i>
                            </Button>
                            {!isSharedClient && (
                              <Button
                                id={`sign-${idx}`}
                                variant='outline-secondary'
                                size='sm'
                                className='me-1'
                                title={t('DS|Sign')}
                                onClick={() => {
                                  setDocumentType('files')
                                  requestSignAccess(file.name)
                                }}
                              >
                                <i className='bi bi-pencil-square'></i>
                              </Button>
                            )}
                          </Col>
                        </Row>
                      ))}
                      {!isSharedClient && (
                        <Row className='my-4'>
                          <Col>
                            <input
                              className='do-not-save d-block mb-3'
                              type='file'
                              name='files[]'
                              id='file'
                              multiple
                              onChange={handleFileChange}
                            ></input>
                            <Button
                              variant='success'
                              className='btn-sm pull-right'
                              type='submit'
                              onClick={() => handleUpload('files')}
                            >
                              {t('Hochladen')}
                            </Button>
                          </Col>
                        </Row>
                      )}
                    </div>
                  )}

                  <Row className='mt-4'>
                    <h5
                      className='bg-collapse text-white p-2 rounded mb-3 cursor-pointer d-flex align-items-center'
                      onClick={() => toggleSection(SECTION_KEYS.DOCUMENTS_CLIENT)}
                    >
                      <FileEarmarkFill />
                      &nbsp;{t('dokumenteMandantKanzlei')}
                      {
                        <b className='ms-auto'>
                          {expandedSections[SECTION_KEYS.DOCUMENTS_CLIENT] ? '▼' : '►'}
                        </b>
                      }
                    </h5>
                  </Row>
                  {expandedSections[SECTION_KEYS.DOCUMENTS_CLIENT] && (
                    <div id='documents-data' className='mb-4'>
                      <Row className='mt-2 border-bottom pb-2'>
                        <Col className='col-sm-3'>
                          <b>{t('users')}</b>
                        </Col>
                        <Col className='col-sm-3'>
                          <b>{t('datumHochladen')}</b>
                        </Col>
                        <Col className='col-sm-3'>
                          <b>{t('file')}</b>
                        </Col>
                        <Col className='col-sm-3'>
                          <b>{t('actions')}</b>
                        </Col>
                      </Row>
                      {companyData.filesClient.map((file, idx) => (
                        <Row key={idx} className='mt-2 border-bottom pb-2'>
                          <Col className='col-sm-3'>{file.user}</Col>
                          <Col className='col-sm-3'>{file.date}</Col>
                          <Col className='col-sm-3'>{file.name}</Col>
                          <Col className='col-sm-3'>
                            <Button
                              variant='outline-secondary'
                              size='sm'
                              className='me-1'
                              title={t('preview')}
                              onClick={() => handleFileView(file.name, 'stammdaten_filesClient')}
                            >
                              <i className='bi bi-eye'></i>
                            </Button>
                            <Button
                              variant='outline-primary'
                              size='sm'
                              className='me-1'
                              title={t('download')}
                              onClick={() =>
                                handleFileDownload(file.name, 'stammdaten_filesClient')
                              }
                            >
                              <i className='bi bi-download'></i>
                            </Button>
                            {!isSharedClient && (
                              <Button
                                id={`sign-${idx}`}
                                variant='outline-secondary'
                                size='sm'
                                className='me-1'
                                title={t('DS|Sign')}
                                onClick={() => {
                                  setDocumentType('filesClient')
                                  requestSignAccess(file.name)
                                }}
                              >
                                <i className='bi bi-pencil-square'></i>
                              </Button>
                            )}
                          </Col>
                        </Row>
                      ))}
                      {isSharedClient && (
                        <Row className='my-4'>
                          <Col>
                            <input
                              className='do-not-save d-block mb-3'
                              type='file'
                              name='filesClient[]'
                              id='file'
                              multiple
                              required
                              onChange={handleFileChange}
                            ></input>
                            <Button
                              variant='success'
                              className='btn-sm pull-right'
                              type='submit'
                              onClick={() => handleUpload('filesClient')}
                            >
                              {t('Hochladen')}
                            </Button>
                          </Col>
                        </Row>
                      )}
                    </div>
                  )}

                  {additionalForms?.some(f => f?.include) ? (
                    additionalForms
                      .filter(f => {
                        return f?.include && f.stammdatenType === 'company'
                      })
                      .map(schema => (
                        <React.Fragment key={schema._id}>
                          <Row className='mt-2'>
                            <h5 className='bg-collapse text-white p-2 rounded mb-3 cursor-pointer d-flex align-items-center'>
                              <div
                                className='d-flex align-items-center flex-grow-1'
                                onClick={() => toggleSchema(schema._id)}
                                role='button'
                                aria-expanded={!!openSchemas[schema._id]}
                                aria-controls={`schema-${schema._id}-content`}
                              >
                                <PlusSquareFill />
                                &nbsp;{schema.name}
                                <b className='ms-auto'>{openSchemas[schema._id] ? '▼' : '►'}</b>
                              </div>
                              <Button
                                variant='light'
                                size='sm'
                                className='ms-2'
                                onClick={e => {
                                  e.stopPropagation()
                                  generateSingleFormPDF(schema._id, schema.name)
                                }}
                              >
                                PDF
                              </Button>
                            </h5>
                          </Row>

                          {(openSchemas[schema._id] || isGeneratingPDF) && (
                            <div id={`schema-${schema._id}-content`} className='mt-2'>
                              <div className='row g-3'>
                                {schema.fields?.map(fld => {
                                  const fieldPath = `additional.dynamic.${schema.name}.${fld.name}`
                                  console.log(fieldPath)
                                  const currentValue = getDeep(companyData, fieldPath)

                                  return (
                                    <div key={`${schema._id}:${fld.name}`} className='col-12'>
                                      <Form.Group className='mb-2'>
                                        {/* TEXT */}
                                        {fld.type === 'text' && (
                                          <>
                                            <Form.Label
                                              className={`fw-semibold small text-muted ${fld.required} ? 'required' : ''`}
                                            >
                                              {fld.name}
                                            </Form.Label>
                                            <Row>
                                              <Col>
                                                <FormControl
                                                  className='form-control'
                                                  name={`${schema.name}.${fld.name}`}
                                                  id={fieldPath}
                                                  type='text'
                                                  value={currentValue || ''}
                                                  onChange={handleChangeLocal}
                                                  required={fld.required}
                                                />
                                              </Col>
                                              <Col>
                                                <button
                                                  type='button'
                                                  className='btn btn-link btn-sm'
                                                  onClick={() =>
                                                    history(`stammdaten.${fieldPath}`, t(''))
                                                  }
                                                >
                                                  <i
                                                    className='fa fa-history text-dark'
                                                    aria-hidden='true'
                                                  ></i>
                                                </button>
                                              </Col>
                                            </Row>
                                          </>
                                        )}

                                        {/* SELECT */}
                                        {fld.type === 'select' && (
                                          <>
                                            <Form.Label
                                              className={`fw-semibold small text-muted ${fld.required} ? 'required' : ''`}
                                            >
                                              {fld.name}
                                            </Form.Label>
                                            <Row>
                                              <Col>
                                                <FormSelect
                                                  className='form-select'
                                                  name={`${schema.name}.${fld.name}`}
                                                  id={fieldPath}
                                                  value={currentValue || ''}
                                                  onChange={handleChangeLocal}
                                                  required={fld.required}
                                                >
                                                  <option value=''>--</option>
                                                  {fld.options?.map((opt: any) => (
                                                    <option key={opt.value} value={opt.value}>
                                                      {opt.label}
                                                    </option>
                                                  ))}
                                                </FormSelect>
                                              </Col>
                                              <Col>
                                                <button
                                                  type='button'
                                                  className='btn btn-link btn-sm'
                                                  onClick={() =>
                                                    history(`stammdaten.${fieldPath}`, t(''))
                                                  }
                                                >
                                                  <i
                                                    className='fa fa-history text-dark'
                                                    aria-hidden='true'
                                                  ></i>
                                                </button>
                                              </Col>
                                            </Row>
                                          </>
                                        )}

                                        {/* CHECKBOX GROUP */}
                                        {fld.type === 'checkbox' && (
                                          <>
                                            <Form.Label
                                              className={`fw-semibold small text-muted d-block mb-2 ${fld.required ? 'required' : ''}`}
                                            >
                                              {fld.name}
                                            </Form.Label>
                                            <div className='d-flex flex-column gap-2 border rounded p-3'>
                                              {fld.options?.map((opt: any, i: number) => {
                                                const path = fieldPath + '.' + opt.label
                                                const current = getDeep(companyData, path)
                                                return (
                                                  <Form.Check
                                                    key={`${schema._id}:${fld.name}:${opt.label}:${i}`}
                                                    type='checkbox'
                                                    id={path}
                                                    name={`${schema.name}.${fld.name}.${opt.label}`}
                                                    label={opt.label ?? opt}
                                                    checked={!!current}
                                                    onChange={handleChangeLocal}
                                                    required={fld.required}
                                                  />
                                                )
                                              })}
                                            </div>
                                            <button
                                              type='button'
                                              className='btn btn-link btn-sm'
                                              onClick={() =>
                                                history(`stammdaten.${fieldPath}`, t(''))
                                              }
                                            >
                                              <i
                                                className='fa fa-history text-dark'
                                                aria-hidden='true'
                                              ></i>
                                            </button>
                                          </>
                                        )}

                                        {/* UPLOAD */}
                                        {fld.type === 'upload' && (
                                          <>
                                            <Form.Label className='fw-semibold small text-muted'>
                                              {fld.name}
                                            </Form.Label>
                                            <Row className='gy-2'>
                                              <Col xs={12}>
                                                <input
                                                  className='do-not-save form-control'
                                                  type='file'
                                                  name='additionalFormFiles'
                                                  id='file'
                                                  multiple
                                                  onChange={handleFileChange}
                                                />
                                                <div className='form-text'>
                                                  {t('chooseFilesToUpload')}
                                                </div>
                                              </Col>
                                              <Col xs='auto'>
                                                <Button
                                                  variant='success'
                                                  className='btn-sm'
                                                  type='submit'
                                                  onClick={() =>
                                                    handleUpload('additionalFormFiles', fieldPath)
                                                  }
                                                >
                                                  {t('Hochladen')}
                                                </Button>
                                              </Col>
                                              <div id='documents-data' className='mb-4'>
                                                <Row className='mt-2 border-bottom pb-2'>
                                                  <Col className='col-sm-3'>
                                                    <b>{t('users')}</b>
                                                  </Col>
                                                  <Col className='col-sm-3'>
                                                    <b>{t('datumHochladen')}</b>
                                                  </Col>
                                                  <Col className='col-sm-3'>
                                                    <b>{t('file')}</b>
                                                  </Col>
                                                  <Col className='col-sm-3'>
                                                    <b>{t('actions')}</b>
                                                  </Col>
                                                </Row>
                                                {toArray(getDeep(companyData, fieldPath)).map(
                                                  (file: FileUpload, idx: number) => (
                                                    <Row
                                                      key={idx}
                                                      className='mt-2 border-bottom pb-2'
                                                    >
                                                      <Col className='col-sm-3'>{file.user}</Col>
                                                      <Col className='col-sm-3'>{file.date}</Col>
                                                      <Col className='col-sm-3'>{file.name}</Col>
                                                      <Col className='col-sm-3'>
                                                        <Button
                                                          variant='outline-secondary'
                                                          size='sm'
                                                          className='me-1'
                                                          title={t('preview')}
                                                          onClick={() =>
                                                            handleFileView(
                                                              file.name,
                                                              'stammdaten_additionalFormFiles'
                                                            )
                                                          }
                                                        >
                                                          <i className='bi bi-eye'></i>
                                                        </Button>
                                                        <Button
                                                          variant='outline-primary'
                                                          size='sm'
                                                          className='me-1'
                                                          title={t('download')}
                                                          onClick={() =>
                                                            handleFileDownload(
                                                              file.name,
                                                              'stammdaten_additionalFormFiles'
                                                            )
                                                          }
                                                        >
                                                          <i className='bi bi-download'></i>
                                                        </Button>
                                                        {!isSharedClient && (
                                                          <Button
                                                            id={`sign-${idx}`}
                                                            variant='outline-secondary'
                                                            size='sm'
                                                            className='me-1'
                                                            title={t('DS|Sign')}
                                                            onClick={() => {
                                                              setDocumentType('files')
                                                              requestSignAccess(file.name)
                                                            }}
                                                          >
                                                            <i className='bi bi-pencil-square'></i>
                                                          </Button>
                                                        )}
                                                      </Col>
                                                    </Row>
                                                  )
                                                )}
                                              </div>
                                            </Row>
                                          </>
                                        )}

                                        {/* RADIO (Question/Answer) */}
                                        {fld.type === 'radio' && (
                                          <>
                                            <Form.Label
                                              className={`fw-semibold small text-muted d-block mb-2 ${fld.required ? 'required' : ''}`}
                                            >
                                              {fld.name}
                                            </Form.Label>
                                            <div className='d-flex flex-column gap-2 border rounded p-3'>
                                              {fld.options?.map((opt: any, i: number) => {
                                                return (
                                                  <Form.Check
                                                    key={`${schema._id}:${fld.name}:${opt.label}:${i}`}
                                                    type='radio'
                                                    id={`${fieldPath}.${opt.label}`}
                                                    name={`${schema.name}.${fld.name}`}
                                                    label={opt.label ?? opt}
                                                    value={opt.label}
                                                    checked={currentValue === opt.label}
                                                    onChange={handleChangeLocal}
                                                    required={fld.required}
                                                  />
                                                )
                                              })}
                                            </div>
                                            <button
                                              type='button'
                                              className='btn btn-link btn-sm'
                                              onClick={() =>
                                                history(`stammdaten.${fieldPath}`, t(''))
                                              }
                                            >
                                              <i
                                                className='fa fa-history text-dark'
                                                aria-hidden='true'
                                              ></i>
                                            </button>
                                          </>
                                        )}
                                      </Form.Group>
                                    </div>
                                  )
                                })}
                              </div>
                            </div>
                          )}
                        </React.Fragment>
                      ))
                  ) : (
                    <p className='text-muted'>
                      {t('No additional schemas') || 'No additional schemas'}
                    </p>
                  )}

                  <hr />
                  <Row className='mt-2'>
                    <KSwitch
                      label={t('stammdatenConfirm')}
                      initialValue={validationModal.agreed}
                      onChange={handleAgreementChange}
                    />
                  </Row>
                </Card.Body>
              </Card>
            </fieldset>
          </form>
        </Row>
      </Container>

      {signModal && (
        <KSignModal
          show={signModal}
          onClose={handleClose}
          onAccept={handleClose}
          firmename={companyData?.name || ''}
          documentName={currentDocument}
          client={companyData || null}
          stammdaten={true}
          stammdatenType={documentType}
        />
      )}
      <HistoryModal
        open={historyModal.isOpen}
        onHide={() =>
          setHistoryModal({
            isOpen: false,
            title: historyModal.title,
            entries: historyModal.entries,
            options: historyModal.options,
          })
        }
        entries={historyModal.entries}
        title={historyModal.title}
        options={historyModal.options}
      />

      <ValidationModal
        open={validationModal.isOpen}
        onHide={() =>
          setValidationModal({
            isOpen: false,
            field: validationModal.field,
            agreed: validationModal.agreed,
          })
        }
        field={validationModal.field}
      />

      {!costConfirmed && (
        <KConfirm
          show={!costConfirmed}
          message={
            <>
              {t('stammdatenCostWarning')}
              {serviceCosts?.stammdaten ? (
                <div className='small mt-1'>
                  <strong>{serviceCosts.stammdaten}</strong>{' '}
                  <strong>{t('punkteWerdenVerrechnet')}</strong>
                </div>
              ) : null}
            </>
          }
          onConfirm={() => setCostConfirmed(true)}
          onCancel={() => navigate('/client/stammdaten')}
        />
      )}

      {access && (
        <KAccess
          show={access}
          title={t('pinCodeSent')}
          pin={pin}
          onConfirm={(pin: any) => {
            checkPin(pin)
          }}
          onClose={() => setAccess(false)}
        />
      )}
      {accessForSign && (
        <KAccess
          show={accessForSign}
          title={pinRequired ? t('pinCodeSent') : t('confirmAction')}
          pin={pin}
          pinRequired={pinRequired}
          onConfirm={(pin: any) => {
            checkPinForSign(pin)
          }}
          onClose={() => setAccessForSign(false)}
        />
      )}

      <KFilePreview target={previewTarget} onClose={() => setPreviewTarget(null)} />
    </>
  )
}
